<template>
  <AdminLayout page="Tenants" title="Tenants">
    <div v-if="isPropertyLoading"><Loading /></div>
    <div v-else-if="userHasPermission">
      <div v-if="tenants && tenants.length > 0">
        <span class="w-full flex my-2" v-if="tenantOrderChanged">
          <p class="grow text-red-700">
            Your sort order changes are unsaved, when done sorting, please hit the 'Save Changes'
            button to save
          </p>
          <SaveButton @click="saveSortedTenants" />
        </span>
        <AddButton @click="addClick">Add Tenant</AddButton>
        <div v-for="tenant in tenants" :key="tenant.name" class="my-2 bg-gray-100 border-t">
          <div class="flex">
            <div class="flex-col mx-2">
              <i-heroicons-outline-chevron-up
                class="h-5 w-5 mt-1 mb-2"
                :class="
                  tenant.sortOrder == Math.min(...tenants.map((t) => t.sortOrder))
                    ? 'text-gray-400'
                    : 'cursor-pointer hover:bg-blue-200'
                "
                @click="swapTenantSort(tenant.sortOrder, true)"
              />
              <i-heroicons-outline-chevron-down
                class="h-5 w-5 mb-1"
                :class="
                  tenant.sortOrder == Math.max(...tenants.map((t) => t.sortOrder))
                    ? 'text-gray-400'
                    : 'cursor-pointer hover:bg-blue-200'
                "
                @click="swapTenantSort(tenant.sortOrder, false)"
              />
            </div>
            <div
              class="cursor-pointer hover:bg-blue-200 flex-grow px-4 py-4"
              @click="editClick(tenant)"
            >
              {{ tenant.name }}
              <span v-if="tenant.isRestricted"> (restricted)</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="mb-2">
        <span>-- No Tenants found for this building --</span>
      </div>
      <div>
        <AddButton @click="addClick">Add Tenant</AddButton>
        <EditButton @click="sortAlpha">Re-Sort Alphabetically</EditButton>
      </div>
      <EditPopup
        entity-name="Tenant"
        :is-visible="isPopupVisible"
        :is-edit-mode="isEditing"
        :save-error="popupError"
        :delete-enabled="true"
        @close="isPopupVisible = false"
        @save="saveClick"
        @delete="beginDelete"
      >
        <BaseInput label="Name" v-model="tenantName" :error="errors.tenantName" />
      </EditPopup>

      <BasePopup
        :is-visible="isDeleteConfirmVisible"
        title="Really Delete Tenant?"
        :is-full-width="true"
        @close="isDeleteConfirmVisible = false"
      >
        <div v-if="tenantUserCount === 0">
          <div>
            Are you sure you want to delete the tenant:
            <span class="font-bold">{{ tenantName }}</span>
          </div>

          <div class="float-right mt-4">
            <button
              class="text-lg font-medium px-4 py-2 mx-4 mt-5 text-blue-900 bg-blue-200 border border-transparent rounded-md hover:bg-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
              @click="deleteClick"
            >
              Yes, delete Tenant
            </button>
            <button
              class="text-lg font-medium px-4 py-2 mt-5 text-blue-900 bg-gray-200 border border-transparent rounded-md hover:bg-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
              @click="isDeleteConfirmVisible = false"
            >
              Cancel
            </button>
          </div>
        </div>
        <div v-else>
          <div>
            Are you sure you want to delete the tenant:
            <span class="font-bold">{{ tenantName }}</span>
          </div>
          <div>
            This tenant has {{ tenantUserCount }} users associated with it. Do you want to also mark
            these users as disabled?
          </div>

          <div class="float-right mt-4">
            <button
              class="text-lg font-medium px-4 py-2 mx-4 mt-5 text-blue-900 bg-blue-200 border border-transparent rounded-md hover:bg-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
              @click="disableTenantUsers"
            >
              Yes, make all {{ tenantUserCount }} users inactive and delete Tenant
            </button>
            <button
              class="text-lg font-medium px-4 py-2 mx-4 mt-5 text-orange-900 bg-orange-200 border border-transparent rounded-md hover:bg-orange-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-orange-500"
              @click="deleteClick"
            >
              Delete the Tenant, but leave the users active
            </button>
            <button
              class="text-lg font-medium px-4 py-2 mt-5 text-blue-900 bg-gray-200 border border-transparent rounded-md hover:bg-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
              @click="isDeleteConfirmVisible = false"
            >
              Cancel
            </button>
          </div>
        </div>
      </BasePopup>
    </div>
    <div v-else>
      <div class="text-center text-2xl font-bold mt-8">
        You do not have permission to view this page
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { PermissionType, SaveTenantCommand, TenantDTO } from "../../store/generatedFetchClient";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useUserStore } from "../../store/userStore";

const bldgStore = useBldgStore();

var isPropertyLoading = computed(() => bldgStore.isLoading);
var property = computed(() => bldgStore.property);
var tenants = ref([] as TenantDTO[]);
var userHasPermission = computed(() => useUserStore().hasPermission(PermissionType.EditTenant));

onMounted(loadData);
watch(property, loadData);

function loadData() {
  if (property.value !== null) {
    // Assign fresh sortOrder values based off the current order (in case of unmigrated)
    let newSortValue = 0;
    tenants.value = bldgStore.property?.tenants.map((tenant) => ({
      ...tenant,
      sortOrder: newSortValue++,
    }));
  }
}

let popupError = ref("");
function onInvalidSubmit(ctx: any) {
  popupError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

let isPopupVisible = ref(false);
let isDeleteConfirmVisible = ref(false);
let isEditing = ref(false);

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    tenantName: yup.string().required().max(100).label("Name"),
    // tenantSystemName: yup.string().required().max(100).label("System Name"),
    // vrLaunchLink: yup.string().notRequired().max(255).label("VR Launch Url"),
    // ssoMatchValues: yup.string().notRequired().max(255).label("Sso Match Values"),
    // emailDomainMatch: yup.string().notRequired().max(50).label("Email Domain Match"),
    // isSsoDefault: yup.boolean().notRequired().label("Default for SSO Login"),
    // isRestricted: yup.boolean().notRequired().label("Hidden on registration form"),
  }),
  initialValues: {
    tenantName: "",
    // tenantSystemName: "",
    // vrLaunchLink: "",
    // ssoMatchValues: "",
    // emailDomainMatch: "",
    // isSsoDefault: false,
    // isRestricted: false,
  },
});

let tenantId = ref(0);
const { value: tenantName } = useField<string>("tenantName");
const { value: tenantSystemName } = useField<string>("tenantSystemName");
const { value: vrLaunchLink } = useField<string>("vrLaunchLink");
const { value: ssoMatchValues } = useField<string>("ssoMatchValues");
const { value: isSsoDefault } = useField<boolean>("isSsoDefault");
const { value: isRestricted } = useField<boolean>("isRestricted");
let sortOrder = ref(-1);
const { value: emailDomainMatch } = useField<string>("emailDomainMatch");

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  let saveTenant = {
    propertyId: property.value?.propertyId,
    portalId: property.value?.portalId,
    tenantId: tenantId.value ?? 0,
    name: tenantName.value,
    systemName: makeSystemName(tenantName.value),
  } as SaveTenantCommand;

  popupError.value = "";

  console.log("saving tenant", saveTenant);
  if (property.value && property.value.portalId) {
    let possibleError = await bldgStore.saveTenant(saveTenant);
    if (!possibleError) {
      isPopupVisible.value = false;
    } else {
      //handle error
      popupError.value = possibleError;
    }
  }
}

let addClick = function () {
  tenantId.value = 0;
  tenantName.value = "";
  tenantSystemName.value = "";
  vrLaunchLink.value = "";
  ssoMatchValues.value = "";
  isSsoDefault.value = false;
  isRestricted.value = false;
  emailDomainMatch.value = "";

  if (top) {
    sortOrder.value = Math.min(...tenants.value.map((t) => t.sortOrder)) - 1;
  } else {
    sortOrder.value = Math.max(...tenants.value.map((t) => t.sortOrder)) + 1;
  }

  popupError.value = "";
  isEditing.value = false;
  isPopupVisible.value = true;
};

let editClick = function (selectedTenant: TenantDTO) {
  tenantId.value = selectedTenant.tenantId ?? 0;
  tenantName.value = selectedTenant.name ?? "";
  tenantSystemName.value = selectedTenant.systemName ?? "";
  vrLaunchLink.value = selectedTenant.vrLaunchUrl ?? "";
  ssoMatchValues.value = selectedTenant.ssoMatchValues ?? "";
  isSsoDefault.value = selectedTenant.isSsoDefault ?? false;
  isRestricted.value = selectedTenant.isRestricted ?? false;
  sortOrder.value = selectedTenant.sortOrder;
  emailDomainMatch.value = selectedTenant.emailDomainMatch ?? "";

  popupError.value = "";
  isEditing.value = true;
  isPopupVisible.value = true;
};

const isDeleting = ref(false);
const tenantUserCount = ref(0);

/// BeginDelete -------------------------------
// Go to the store and get the tenant count to display in warning dialog
async function beginDelete() {
  tenantUserCount.value = await bldgStore.getTenantUserCount(tenantId.value);
  if (tenantUserCount.value >= 0) {
    isDeleteConfirmVisible.value = true;
  } else {
    popupError.value = "Error getting user count for tenant, please try again";
  }
}

async function deleteClick() {
  isDeleteConfirmVisible.value = false;
  if (isEditing.value && tenantId.value > 0) {
    isDeleting.value = true;
    popupError.value = await bldgStore.deleteTenant(tenantId.value);
    isDeleting.value = false;
    if (!popupError.value) {
      isPopupVisible.value = false;
      loadData();
    }
  }
}

async function disableTenantUsers() {
  isDeleteConfirmVisible.value = false;
  if (isEditing.value && tenantId.value > 0) {
    isDeleting.value = true;
    popupError.value = await bldgStore.disableTenantUsers(tenantId.value);
    isDeleting.value = false;
    if (!popupError.value) {
      deleteClick();
    }
  }
}

// Reordering -------------------------------

var tenantOrderChanged = ref(false);

function swapTenantSort(sortID: number, sortUp: boolean) {
  let selectedTenantID = tenants.value.find((t) => t.sortOrder == sortID).tenantId;

  if (sortUp) {
    let swapTenantID = tenants.value.find((t) => t.sortOrder == sortID - 1).tenantId;
    tenants.value.find((t) => t.tenantId == selectedTenantID).sortOrder = sortID - 1;
    tenants.value.find((t) => t.tenantId == swapTenantID).sortOrder = sortID;

    tenantOrderChanged.value = true;
  } else {
    let swapTenantID = tenants.value.find((t) => t.sortOrder == sortID + 1).tenantId;
    tenants.value.find((t) => t.tenantId == selectedTenantID).sortOrder = sortID + 1;
    tenants.value.find((t) => t.tenantId == swapTenantID).sortOrder = sortID;

    tenantOrderChanged.value = true;
  }

  sortAllTenants();
}

function sortAlpha() {
  tenants.value.sort((x, y) => {
    if (x.name.toLowerCase() < y.name.toLowerCase()) {
      return -1;
    } else if (x.name.toLowerCase() > y.name.toLowerCase()) {
      return 1;
    }
    return 0;
  });

  for (var i = 0; i < tenants.value.length; i++) {
    tenants.value[i].sortOrder = i;
  }
  tenantOrderChanged.value = true;
  console.log("Tenants sorted alphabetically", tenants.value);
}

function sortAllTenants() {
  tenants.value.sort((x, y) => {
    if (x.sortOrder < y.sortOrder) {
      return -1;
    } else if (x.sortOrder > y.sortOrder) {
      return 1;
    }
    return 0;
  });
}

async function saveSortedTenants() {
  let orderToSave = {
    tenantOrders: tenants.value.map((t) => ({
      tenantId: t.tenantId,
      sortOrder: t.sortOrder,
    })),
  };

  popupError.value = await bldgStore.saveTenantOrder(orderToSave);
  if (popupError.value) return;

  tenantOrderChanged.value = false;
  loadData();
}

function makeSystemName(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .substring(0, 100);
}
</script>
